import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Calculator, Receipt, Users, Briefcase, Shield, Landmark, Scale, FileCheck2, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { companyInfo, services, aboutContent, testimonials } from '../data/mock';
import AnimatedHero from '../components/AnimatedHero';

const Home = () => {
  const iconMap = {
    Building2,
    Calculator,
    Receipt,
    Users,
    Briefcase,
    Shield,
    Landmark,
    Scale,
    FileCheck2
  };

  const visibleStats = useMemo(
    () => aboutContent.stats.filter((stat) => stat.label !== 'Happy Clients'),
    []
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Hero Video Section */}
      <AnimatedHero />

      {/* Hero Section with CTA */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758518730037-a16581a040e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc3MTA2MDU0M3ww&ixlib=rb-4.1.0&q=80&auto=format&fit=crop&w=960"
            alt="Business consultants discussing corporate strategy in a Dubai office"
            className="w-full h-full object-cover opacity-20"
            loading="lazy"
            decoding="async"
            width="1280"
            height="720"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black"></div>
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 py-32">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-[#B19CD9]/20 text-[#B19CD9] border-[#B19CD9]/30 hover:bg-[#B19CD9]/30">
              Trusted Corporate Services Provider
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Empowering Your
              <span className="text-[#B19CD9]"> Business Growth</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              {companyInfo.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold text-lg group"
              >
                <Link to="/services">
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-[#B19CD9] text-[#B19CD9] hover:bg-[#B19CD9] hover:text-black transition-all duration-300 text-lg"
              >
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mt-20">
              {visibleStats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-[#B19CD9] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#B19CD9]/20 text-[#B19CD9] border-[#B19CD9]/30">
              Our Services
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Comprehensive <span className="text-[#B19CD9]">Corporate Solutions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From company formation to ongoing compliance, we provide end-to-end corporate services tailored to your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="bg-gray-900/50 border-white/10 hover:border-[#B19CD9]/50 transition-all duration-300 group hover:transform hover:scale-105"
                >
                  <CardHeader>
                    <div className="w-14 h-14 rounded-lg bg-[#B19CD9]/20 flex items-center justify-center mb-4 group-hover:bg-[#B19CD9]/30 transition-colors">
                      <Icon className="h-7 w-7 text-[#B19CD9]" />
                    </div>
                    <CardTitle className="text-xl text-white group-hover:text-[#B19CD9] transition-colors">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature) => (
                        <li key={`${service.id}-${feature}`} className="flex items-center text-sm text-gray-400">
                          <CheckCircle className="h-4 w-4 text-[#B19CD9] mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {service.title === 'Accounting & Bookkeeping' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-accounting-read-more-link"
                        >
                          <Link to="/accounting-bookkeeping-services-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Company Formation & Registration' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-company-setup-read-more-link"
                        >
                          <Link to="/company-setup-freezone-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Tax Services' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-tax-services-read-more-link"
                        >
                          <Link to="/tax-services-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'HR Services' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-hr-services-read-more-link"
                        >
                          <Link to="/hr-services-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Management Consultancy' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-management-services-read-more-link"
                        >
                          <Link to="/management-consultancy-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Corporate Governance & Compliance' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-governance-services-read-more-link"
                        >
                          <Link to="/corporate-governance-compliance-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Legal Assistance' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-legal-services-read-more-link"
                        >
                          <Link to="/legal-assistance-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Audit Assistance' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-audit-services-read-more-link"
                        >
                          <Link to="/audit-assistance-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}

                    {service.title === 'Bank Account Opening' && (
                      <div className="mt-5">
                        <Button
                          asChild
                          size="sm"
                          className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
                          data-testid="home-bank-account-read-more-link"
                        >
                          <Link to="/corporate-services-dubai-uae">Read More</Link>
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button
              asChild
              size="lg"
              className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold"
            >
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-[#B19CD9]/20 text-[#B19CD9] border-[#B19CD9]/30">
                Why Choose Us
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Your Trusted Partner for <span className="text-[#B19CD9]">Corporate Excellence</span>
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                {aboutContent.mission}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {aboutContent.values.map((value) => (
                  <div key={value.title} className="space-y-2">
                    <h3 className="text-xl font-semibold text-[#B19CD9]">{value.title}</h3>
                    <p className="text-gray-400 text-sm">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc3MTA2MDU0M3ww&ixlib=rb-4.1.0&q=80&auto=format&fit=crop&w=960"
                alt="SUM-IT advisors collaborating with clients in a business meeting"
                className="rounded-lg shadow-2xl"
                loading="lazy"
                decoding="async"
                width="1200"
                height="800"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#B19CD9]/20 text-[#B19CD9] border-[#B19CD9]/30">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What Our <span className="text-[#B19CD9]">Clients Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="bg-gray-900/50 border-white/10 hover:border-[#B19CD9]/50 transition-all duration-300"
              >
                <CardHeader>
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.id}-star-${i + 1}`} className="h-5 w-5 text-[#B19CD9] fill-[#B19CD9]" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-300 text-base leading-relaxed">
                    &ldquo;{testimonial.content}&rdquo;
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">
                      {testimonial.role}, {testimonial.company}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[#B19CD9]" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 100%)' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to <span className="text-[#B19CD9]">Get Started?</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Let&apos;s discuss how we can help your business thrive with our comprehensive corporate services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-[#B19CD9] text-black hover:bg-[#9B7CC9] transition-all duration-300 font-semibold text-lg"
              >
                <Link to="/contact">Contact Us Today</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-[#B19CD9] text-[#B19CD9] hover:bg-[#B19CD9] hover:text-black transition-all duration-300 text-lg"
              >
                <Link to="/services">Explore Services</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
